import type { ReferralRow, ReferralStatus } from '@nono/contract';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { api, queryClient } from '@/lib/api';
import { type Locale, useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/*
 * Verving (Nono-dugnaden) — the staff tool for the referral program.
 *
 *  1) Reward-køen: who has EARNED their free months (status 'kvalifisert').
 *     «Gitt»-knappen presses ONLY after the credit is actually in Momence.
 *  2) Pipeline: every referral and where it stands (all channels, one truth).
 *  3) Manual entry: the verbal referral captured at the desk («Grete sa at
 *     Kari tipset henne») — the PRIMARY channel per the stress test.
 *  4) Internt leaderboard — never shown in the lokale (Jantelov-regelen).
 */

interface VervingMessages {
  /** keys are contract enum values — only the labels are translated */
  statusLabel: Record<string, string>;
  channelLabel: Record<string, string>;

  pageTitle: string;
  pageSub: string;
  noData: string;

  statTotalLabel: string;
  statTotalSub: string;
  statMembersLabel: string;
  statMembersSub: string;
  statAwaitingLabel: string;
  statAwaitingSub: string;
  statMonthsLabel: string;
  statMonthsSub: string;

  queueTitle: string;
  queueSub: string;
  referred: string;
  noBooking: string;
  confirmQuestion: string;
  confirmYes: string;
  evidencePlaceholder: string;
  cancel: string;
  markGiven: string;

  createTitle: string;
  createSub: string;
  phReferrerName: string;
  phFriendName: string;
  phFriendPhone: string;
  phBookingRef: string;
  saving: string;
  save: string;
  saveError: string;

  tableTitle: string;
  emptyTitle: string;
  emptyBody: string;
  colDate: string;
  colReferrer: string;
  colFriend: string;
  colChannel: string;
  colStatus: string;
  colFlags: string;
  colAction: string;
  flagAdOverlap: string;
  flagHousehold: string;
  disqualify: string;
  revoke: string;
  disqualifyPrompt: string;
  revokePrompt: string;

  leaderboardTitle: string;
  leaderboardSub: string;
  leaderboardRow: (total: number, given: number) => string;
}

const MESSAGES: Record<Locale, VervingMessages> = {
  nb: {
    statusLabel: {
      registrert: 'Registrert',
      booket: 'Booket prøvetime',
      medlem: 'Ble medlem',
      kvalifisert: '🎁 Belønning utløst',
      gitt: 'Gitt ✓',
      diskvalifisert: 'Diskvalifisert',
      tilbakekalt: 'Tilbakekalt',
    },
    channelLabel: {
      link: 'Lenke',
      card: 'Kort',
      hint: 'Booket!-svar',
      staff: 'Vertskapet',
    },

    pageTitle: 'Verving · Nono-dugnaden',
    pageSub:
      'Alle vervinger, uansett kanal, går gjennom samme løp. «Gitt» trykkes først når kreditten faktisk ligger i Momence («Vervegave: −899 kr» på neste faktura).',
    noData: 'Ingen data',

    statTotalLabel: 'Vervinger totalt',
    statTotalSub: 'siste 12 mnd',
    statMembersLabel: 'Ble medlem',
    statMembersSub: 'via verving',
    statAwaitingLabel: 'Venter på gave',
    statAwaitingSub: 'kvalifiserte i køen',
    statMonthsLabel: 'Gratismåneder brukt',
    statMonthsSub: 'hardt tak, eier kan utvide',

    queueTitle: '🎁 Reward-køen — mandagsrutinen',
    queueSub:
      '1) Legg kreditt på BEGGE fakturaene i Momence (verver + venn). 2) Trykk «Gitt». Aldri omvendt rekkefølge.',
    referred: ' vervet ',
    noBooking: 'uten booking',
    confirmQuestion: 'Ligger kreditten i Momence?',
    confirmYes: 'Ja, marker som gitt',
    evidencePlaceholder: 'Faktura-/notatreferanse i Momence *',
    cancel: 'Avbryt',
    markGiven: 'Gitt…',

    createTitle: 'Registrer en verving',
    createSub:
      'Den muntlige kanalen er den største: «Hvem tipset deg om oss?» ved prøvetimen. Skriv den inn her, samme løp som lenke og kort.',
    phReferrerName: 'Ververens navn (medlemmet) *',
    phFriendName: 'Vennens navn',
    phFriendPhone: 'Vennens mobil (valgfritt)',
    phBookingRef: 'Booking-ref (NONO-…, valgfritt)',
    saving: 'Lagrer…',
    save: 'Registrer',
    saveError: 'Kunne ikke lagre, prøv igjen.',

    tableTitle: 'Alle vervinger',
    emptyTitle: 'Ingen vervinger ennå',
    emptyBody: 'De dukker opp her fra lenker, kort, Booket!-svar og manuell registrering.',
    colDate: 'Dato',
    colReferrer: 'Verver',
    colFriend: 'Venn',
    colChannel: 'Kanal',
    colStatus: 'Status',
    colFlags: 'Flagg',
    colAction: 'Handling',
    flagAdOverlap: 'annonse-overlapp',
    flagHousehold: 'husstand',
    disqualify: 'Diskvalifiser',
    revoke: 'Tilbakekall',
    disqualifyPrompt: 'Skriv den konkrete grunnen (minst 8 tegn). Endringen kan ikke reverseres:',
    revokePrompt: 'Skriv dokumentasjon for tilbakekallingen (minst 8 tegn):',

    leaderboardTitle: 'Internt leaderboard',
    leaderboardSub:
      'Kun her. Aldri på veggen i lokalet — heder skjer stille, og bare med samtykke.',
    leaderboardRow: (total: number, given: number) => `${total} vervinger · ${given} gitt`,
  },
  en: {
    statusLabel: {
      registrert: 'Registered',
      booket: 'Booked trial session',
      medlem: 'Joined',
      kvalifisert: '🎁 Reward unlocked',
      gitt: 'Given ✓',
      diskvalifisert: 'Disqualified',
      tilbakekalt: 'Revoked',
    },
    channelLabel: {
      link: 'Link',
      card: 'Card',
      hint: 'Booked! reply',
      staff: 'The hosts',
    },

    pageTitle: 'Referrals · Nono-dugnaden',
    pageSub:
      'Every referral, whatever the channel, runs through the same flow. "Given" is pressed only once the credit is actually in Momence («Vervegave: −899 kr» on the next invoice).',
    noData: 'No data',

    statTotalLabel: 'Referrals total',
    statTotalSub: 'last 12 months',
    statMembersLabel: 'Joined',
    statMembersSub: 'via referral',
    statAwaitingLabel: 'Awaiting reward',
    statAwaitingSub: 'qualified in the queue',
    statMonthsLabel: 'Free months used',
    statMonthsSub: 'hard cap, the owner can raise it',

    queueTitle: '🎁 The reward queue — the Monday routine',
    queueSub:
      '1) Put the credit on BOTH invoices in Momence (referrer + friend). 2) Press "Given". Never the other way around.',
    referred: ' referred ',
    noBooking: 'no booking',
    confirmQuestion: 'Is the credit in Momence?',
    confirmYes: 'Yes, mark as given',
    evidencePlaceholder: 'Momence invoice/note reference *',
    cancel: 'Cancel',
    markGiven: 'Given…',

    createTitle: 'Log a referral',
    createSub:
      'The word-of-mouth channel is the biggest: "Who told you about us?" at the trial session. Type it in here, same flow as link and card.',
    phReferrerName: "Referrer's name (the member) *",
    phFriendName: "Friend's name",
    phFriendPhone: "Friend's mobile (optional)",
    phBookingRef: 'Booking ref (NONO-…, optional)',
    saving: 'Saving…',
    save: 'Register',
    saveError: 'Could not save, please try again.',

    tableTitle: 'All referrals',
    emptyTitle: 'No referrals yet',
    emptyBody: 'They show up here from links, cards, Booked! replies and manual entry.',
    colDate: 'Date',
    colReferrer: 'Referrer',
    colFriend: 'Friend',
    colChannel: 'Channel',
    colStatus: 'Status',
    colFlags: 'Flags',
    colAction: 'Action',
    flagAdOverlap: 'ad overlap',
    flagHousehold: 'household',
    disqualify: 'Disqualify',
    revoke: 'Revoke',
    disqualifyPrompt: 'Enter the concrete reason (at least 8 characters). This cannot be reversed:',
    revokePrompt: 'Enter evidence for the revocation (at least 8 characters):',

    leaderboardTitle: 'Internal leaderboard',
    leaderboardSub:
      'Here only. Never on the wall in the studio — recognition happens quietly, and only with consent.',
    leaderboardRow: (total: number, given: number) => `${total} referrals · ${given} given`,
  },
};

const STATUS_TONE: Record<string, string> = {
  registrert: 'bg-bg-muted text-fg-subtle',
  booket: 'bg-accent-click/15 text-accent-click',
  medlem: 'bg-accent-booking/15 text-accent-booking',
  kvalifisert: 'bg-warn-bg text-warn-fg',
  gitt: 'bg-good-bg text-good-fg',
  diskvalifisert: 'bg-bad-bg text-bad-fg',
  tilbakekalt: 'bg-bad-bg text-bad-fg',
};

function StatusChip({ status }: { status: string }) {
  const m = useViewMessages(MESSAGES);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        STATUS_TONE[status] ?? 'bg-bg-muted',
      )}
    >
      {m.statusLabel[status] ?? status}
    </span>
  );
}

export function VervingPage() {
  const m = useViewMessages(MESSAGES);
  const q = useQuery({ queryKey: ['referrals'], queryFn: () => api.referrals(365) });
  const [form, setForm] = useState({
    referrerName: '',
    friendName: '',
    friendPhone: '',
    bookingRef: '',
    notes: '',
  });
  const [confirmGitt, setConfirmGitt] = useState<number | null>(null);
  const [statusEvidence, setStatusEvidence] = useState('');

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['referrals'] });
  const createMut = useMutation({
    mutationFn: () =>
      api.referralCreate({
        referrerName: form.referrerName.trim(),
        friendName: form.friendName.trim() || undefined,
        friendPhone: form.friendPhone.trim() || undefined,
        bookingRef: form.bookingRef.trim() || undefined,
        channel: 'staff',
        notes: form.notes.trim() || undefined,
      }),
    onSuccess: () => {
      setForm({ referrerName: '', friendName: '', friendPhone: '', bookingRef: '', notes: '' });
      invalidate();
    },
  });
  const statusMut = useMutation({
    mutationFn: (args: { id: number; status: ReferralStatus; note: string }) =>
      api.referralStatus(args.id, { status: args.status, note: args.note }),
    onSuccess: invalidate,
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState onRetry={() => void q.refetch()} />;
  const d = q.data;
  if (!d) return <EmptyState title={m.noData} />;

  const nyeMedlemmer =
    (d.byStatus.medlem ?? 0) + (d.byStatus.kvalifisert ?? 0) + (d.byStatus.gitt ?? 0);

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold">{m.pageTitle}</h1>
        <p className="text-sm text-fg-subtle">{m.pageSub}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label={m.statTotalLabel}
          value={String(d.rows.length)}
          sub={m.statTotalSub}
          source="derived"
        />
        <StatCard
          label={m.statMembersLabel}
          value={String(nyeMedlemmer)}
          sub={m.statMembersSub}
          source="momence"
        />
        <StatCard
          label={m.statAwaitingLabel}
          value={String(d.rewardQueue.length)}
          sub={m.statAwaitingSub}
          source="derived"
          status={d.rewardQueue.length > 0 ? 'warn' : 'good'}
        />
        <StatCard
          label={m.statMonthsLabel}
          value={`${d.creditMonthsGiven}/${d.creditMonthsCap}`}
          sub={m.statMonthsSub}
          source="derived"
          status={d.creditMonthsGiven >= d.creditMonthsCap ? 'bad' : 'good'}
        />
      </div>

      {d.rewardQueue.length > 0 && (
        <Card className="border-warn-fg/40 p-4">
          <h2 className="mb-1 text-lg font-semibold">{m.queueTitle}</h2>
          <p className="mb-3 text-sm text-fg-subtle">{m.queueSub}</p>
          <div className="flex flex-col gap-2">
            {d.rewardQueue.map((r: ReferralRow) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-2"
              >
                <div className="text-sm">
                  <span className="font-semibold">{r.referrerName}</span>
                  <span className="text-fg-subtle">{m.referred}</span>
                  <span className="font-semibold">{r.friendName ?? '?'}</span>
                  <span className="text-fg-subtle">
                    {' '}
                    · {m.channelLabel[r.channel]} · {r.bookingRef ?? m.noBooking}
                  </span>
                </div>
                {confirmGitt === r.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-fg-subtle">{m.confirmQuestion}</span>
                    <input
                      className="min-w-52 rounded-md border border-border bg-bg px-2 py-1.5 text-xs"
                      placeholder={m.evidencePlaceholder}
                      value={statusEvidence}
                      onChange={(e) => setStatusEvidence(e.target.value)}
                    />
                    <button
                      type="button"
                      disabled={statusEvidence.trim().length < 8 || statusMut.isPending}
                      className="rounded-md bg-good-bg px-3 py-1.5 text-xs font-bold text-good-fg disabled:opacity-40"
                      onClick={() => {
                        statusMut.mutate({ id: r.id, status: 'gitt', note: statusEvidence.trim() });
                        setConfirmGitt(null);
                        setStatusEvidence('');
                      }}
                    >
                      {m.confirmYes}
                    </button>
                    <button
                      type="button"
                      className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold"
                      onClick={() => {
                        setConfirmGitt(null);
                        setStatusEvidence('');
                      }}
                    >
                      {m.cancel}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="rounded-md border border-border px-3 py-1.5 text-xs font-bold"
                    onClick={() => {
                      setConfirmGitt(r.id);
                      setStatusEvidence('');
                    }}
                  >
                    {m.markGiven}
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <h2 className="mb-1 text-lg font-semibold">{m.createTitle}</h2>
        <p className="mb-3 text-sm text-fg-subtle">{m.createSub}</p>
        <div className="grid gap-2 md:grid-cols-2">
          <input
            className="rounded-lg border border-border bg-bg px-3 py-2 text-sm"
            placeholder={m.phReferrerName}
            value={form.referrerName}
            onChange={(e) => setForm({ ...form, referrerName: e.target.value })}
          />
          <input
            className="rounded-lg border border-border bg-bg px-3 py-2 text-sm"
            placeholder={m.phFriendName}
            value={form.friendName}
            onChange={(e) => setForm({ ...form, friendName: e.target.value })}
          />
          <input
            className="rounded-lg border border-border bg-bg px-3 py-2 text-sm"
            placeholder={m.phFriendPhone}
            value={form.friendPhone}
            onChange={(e) => setForm({ ...form, friendPhone: e.target.value })}
          />
          <input
            className="rounded-lg border border-border bg-bg px-3 py-2 text-sm"
            placeholder={m.phBookingRef}
            value={form.bookingRef}
            onChange={(e) => setForm({ ...form, bookingRef: e.target.value })}
          />
        </div>
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            disabled={form.referrerName.trim().length < 2 || createMut.isPending}
            className="rounded-lg bg-fg px-4 py-2 text-sm font-bold text-bg disabled:opacity-40"
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? m.saving : m.save}
          </button>
          {createMut.isError && <span className="text-sm text-bad-fg">{m.saveError}</span>}
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="mb-3 text-lg font-semibold">{m.tableTitle}</h2>
        {d.rows.length === 0 ? (
          <EmptyState title={m.emptyTitle}>{m.emptyBody}</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-fg-subtle">
                  <th className="py-2 pr-3">{m.colDate}</th>
                  <th className="py-2 pr-3">{m.colReferrer}</th>
                  <th className="py-2 pr-3">{m.colFriend}</th>
                  <th className="py-2 pr-3">{m.colChannel}</th>
                  <th className="py-2 pr-3">{m.colStatus}</th>
                  <th className="py-2 pr-3">{m.colFlags}</th>
                  <th className="py-2">{m.colAction}</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r: ReferralRow) => (
                  <tr key={r.id} className="border-b border-border/60">
                    <td className="py-2 pr-3 whitespace-nowrap text-fg-subtle">
                      {r.createdAt.slice(0, 10)}
                    </td>
                    <td className="py-2 pr-3 font-medium">{r.referrerName}</td>
                    <td className="py-2 pr-3">
                      {r.friendName ?? '—'}
                      <span className="text-fg-subtle">
                        {r.bookingRef ? ` · ${r.bookingRef}` : ''}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{m.channelLabel[r.channel]}</td>
                    <td className="py-2 pr-3">
                      <StatusChip status={r.status} />
                    </td>
                    <td className="py-2 pr-3">
                      {r.adOverlapFlag && <Badge>{m.flagAdOverlap}</Badge>}
                      {r.householdFlag && <Badge>{m.flagHousehold}</Badge>}
                    </td>
                    <td className="py-2">
                      {(r.status === 'booket' ||
                        r.status === 'registrert' ||
                        r.status === 'medlem') && (
                        <button
                          type="button"
                          className="text-xs font-semibold text-bad-fg underline"
                          onClick={() => {
                            const reason = window.prompt(m.disqualifyPrompt)?.trim();
                            if (reason && reason.length >= 8)
                              statusMut.mutate({
                                id: r.id,
                                status: 'diskvalifisert',
                                note: reason,
                              });
                          }}
                        >
                          {m.disqualify}
                        </button>
                      )}
                      {r.status === 'gitt' && (
                        <button
                          type="button"
                          className="text-xs font-semibold text-bad-fg underline"
                          onClick={() => {
                            const reason = window.prompt(m.revokePrompt)?.trim();
                            if (reason && reason.length >= 8)
                              statusMut.mutate({ id: r.id, status: 'tilbakekalt', note: reason });
                          }}
                        >
                          {m.revoke}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {d.leaderboard.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-1 text-lg font-semibold">{m.leaderboardTitle}</h2>
          <p className="mb-3 text-sm text-fg-subtle">{m.leaderboardSub}</p>
          <div className="flex flex-col gap-1">
            {d.leaderboard.map(
              (l: { name: string; total: number; kvalifisert: number; gitt: number }) => (
                <div
                  key={l.name}
                  className="flex items-center justify-between rounded px-2 py-1.5 text-sm odd:bg-bg-subtle"
                >
                  <span className="font-medium">{l.name}</span>
                  <span className="text-fg-subtle">{m.leaderboardRow(l.total, l.gitt)}</span>
                </div>
              ),
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
